/**
 * aba-tiny.tsx — A aba "Tiny" do detalhe da despesa (ficha #1).
 *
 * O que vai para o Tiny fica AO LADO da despesa, nunca por cima: o histórico
 * de quem lançou continua na aba Despesa, e aqui se escreve o do Tiny. Grava
 * por rota própria (`PUT /despesas/{id}/tiny`) — salvar a despesa não mexe aqui.
 *
 * ─── O CADEADO ───────────────────────────────────────────────────────────────
 * Dele, 25/09: o único critério de "lançada" é o id do Tiny na resposta. Sem
 * id, a despesa trava e só se destrava à mão, depois de conferir no Tiny. É o
 * bloco vermelho do lado direito, com os dois botões: "entrou" (com o número
 * de lá) e "não entrou".
 *
 * ⛔ Nada aqui escreve no Tiny sem `TINY_ESCRITA_ATIVA=1` no servidor.
 * Desligado, o botão diz "Simular envio" e a resposta é só a prévia.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'sonner';
import { useAuth } from '@/auth/auth-context';
import { CustomCampoData, CustomCampoMesAno } from '@/components/CustomCamposData';
import { CustomCampoNumeroMoeda } from '@/components/CustomCamposNumero';
import { doBanco, paraBanco } from '@/lib/numero';
import { api, ApiError } from '@/lib/api';
import { formatarData } from '@/lib/data';
import { formatarValor } from './shared';

type EstadoEnvio = 'nao_enviada' | 'simulada' | 'enviando' | 'enviada' | 'verificar' | 'ja_no_tiny';

interface Previa {
  api: string;
  payload: Record<string, unknown>;
  marca: boolean;
  disponivel: boolean;
  escrita_ativa: boolean;
  numero_reservado: boolean;
}

interface EstadoTiny {
  competencia: string | null;
  historico_tiny: string | null;
  categoria_tiny: string | null;
  contato_tiny: string | null;
  envio_tiny: EstadoEnvio;
  api_envio_tiny: string | null;
  numero_documento_tiny: string | null;
  conta_pagar_tiny: string | null;
  marcador_tiny: string | null;
  enviado_tiny_em: string | null;
  erro_envio_tiny: string | null;
  situacao_tiny: string | null;
  conferido_tiny_em: string | null;
  /** O corpo EXATO que foi (ou iria) para o Tiny — gravado antes do envio. */
  payload_envio_tiny: Record<string, unknown> | null;
  enviado_por: string | null;
  /** Quem conferiu antes do Tiny, e quando (migration 115). */
  conferida_em: string | null;
  conferida_por: string | null;
  data: string | null;
  historico: string | null;
  /** Parcela (migration 117): vencimento próprio e "n de N". */
  vencimento: string | null;
  parcela_numero: number | null;
  parcela_quantidade: number | null;
  /** Vai também o marcador `cartão-adriana` (migration 118). */
  cartao_adriana: boolean;
  travada: boolean;
  cadeado: boolean;
  escrita_ativa: boolean;
  previa: Previa | null;
  falta: string | null;
}

interface Opcoes {
  categorias: { id: string; nome: string; grupo: string | null }[];
  contatos: { id: string; nome: string; quando_usar: string | null }[];
}

const ROTULO_ESTADO: Record<EstadoEnvio, { texto: string; classe: string; icone: string }> = {
  nao_enviada: { texto: 'Não enviada', classe: 'text-bg-light border', icone: 'bi-circle' },
  simulada: { texto: 'Simulada', classe: 'text-bg-light border', icone: 'bi-eye' },
  enviando: { texto: 'Com cadeado', classe: 'text-bg-danger', icone: 'bi-lock-fill' },
  verificar: { texto: 'Com cadeado', classe: 'text-bg-danger', icone: 'bi-lock-fill' },
  enviada: { texto: 'No Tiny', classe: 'text-bg-success', icone: 'bi-check-circle-fill' },
  ja_no_tiny: { texto: 'Já estava no Tiny', classe: 'text-bg-success', icone: 'bi-check2-all' },
};

/** 'AAAA-MM-DD' → Date no dia 1 (o campo é de mês/ano). */
const isoParaMes = (iso: string | null) => {
  if (!iso) return null;
  const [a, m] = iso.split('-').map(Number);
  return new Date(a, m - 1, 1);
};
const mesParaIso = (d: Date | null) =>
  d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01` : null;

/** Os campos do corpo enviado, com o nome que se lê (v3 e v2). */
const ROTULO_CAMPO: Record<string, string> = {
  valor: 'Valor',
  data: 'Emissão',
  dataVencimento: 'Vencimento',
  vencimento: 'Vencimento',
  dataCompetencia: 'Competência',
  competencia: 'Competência',
  categoria: 'Categoria',
  contato: 'Contato',
  cliente: 'Contato',
  historico: 'Histórico',
  numeroDocumento: 'Documento',
  nro_documento: 'Documento',
  ocorrencia: 'Ocorrência',
};

function mostrarCampo(chave: string, v: unknown, opcoes: Opcoes): string {
  if (chave === 'valor') return formatarValor(Number(v));
  if (chave === 'ocorrencia') return v === 'U' ? 'única' : String(v);
  if (chave === 'categoria' && typeof v === 'object' && v) {
    const id = String((v as { id?: unknown }).id ?? '');
    const c = opcoes.categorias.find((x) => x.id === id);
    return c ? `${c.nome} (${id})` : id;
  }
  if ((chave === 'contato' || chave === 'cliente') && typeof v === 'object' && v) {
    const o = v as { id?: unknown; nome?: unknown };
    const id = String(o.id ?? '');
    const c = opcoes.contatos.find((x) => x.id === id);
    return c ? `${c.nome} (${id})` : String(o.nome ?? id);
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(v))) return formatarData(String(v));
  // Competência vai como 'AAAA-MM' — lê-se 09/2026.
  const mes = /^(\d{4})-(\d{2})$/.exec(String(v));
  if (mes) return `${mes[2]}/${mes[1]}`;
  return String(v);
}

interface EstadoBaixa {
  baixa_tiny: 'nao_baixada' | 'baixando' | 'baixada' | 'verificar';
  pode_baixar: boolean;
  contas: { id: string; nome: string }[];
  sugerida: string | null;
  valor_sugerido: number;
  data_sugerida: string | null;
  conta: string | null;
  conta_nome: string | null;
  data_baixa: string | null;
  valor_baixa: number | null;
  erro: string | null;
  baixada_em: string | null;
  baixada_por: string | null;
}

/**
 * DAR BAIXA (migration 114). Dele, 25/09: "tem que ser na conta certa — e é
 * por loja". A conta vem da lista DAQUELA loja no Tiny, sugerida pela forma de
 * pagamento, e quem baixa CONFIRMA — a confirmação repete o nome da conta.
 */
function BlocoBaixa({ despesaId, aoMudar }: { despesaId: string; aoMudar: () => void }) {
  const { pode } = useAuth();
  const [b, setB] = useState<EstadoBaixa | null>(null);
  const [conta, setConta] = useState('');
  const [data, setData] = useState('');
  const [valor, setValor] = useState('');
  const [confirmar, setConfirmar] = useState(false);
  const [baixando, setBaixando] = useState(false);

  async function carregar() {
    try {
      const e = await api.get<EstadoBaixa>(`/despesas/${despesaId}/tiny/baixa`);
      setB(e);
      setConta(e.sugerida ?? '');
      setData(e.data_sugerida ?? '');
      setValor(doBanco(e.valor_sugerido));
    } catch {
      /* sem bloco */
    }
  }
  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [despesaId]);

  async function baixar() {
    setBaixando(true);
    try {
      const e = await api.post<EstadoBaixa>(`/despesas/${despesaId}/tiny/baixar`, {
        conta_financeira: conta,
        data,
        valor: paraBanco(valor),
      });
      setB(e);
      toast.success('Baixa feita — o Tiny confirmou a conta paga.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível dar baixa.');
      carregar();
    } finally {
      setBaixando(false);
      setConfirmar(false);
      aoMudar();
    }
  }

  async function reabrir(foiPaga: boolean) {
    try {
      setB(await api.post<EstadoBaixa>(`/despesas/${despesaId}/tiny/baixa/reabrir`, { foi_paga: foiPaga }));
      aoMudar();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível abrir o cadeado.');
    }
  }

  if (!b) return null;
  const nomeConta = b.contas.find((c) => c.id === conta)?.nome;
  const travada = b.baixa_tiny === 'verificar' || b.baixa_tiny === 'baixando';

  return (
    <div className="card adminuiux-card mb-3">
      <div className="card-header bg-transparent d-flex align-items-center gap-2">
        <i className="bi bi-cash-coin"></i>
        <h6 className="mb-0 flex-grow-1">Baixa</h6>
        {b.baixa_tiny === 'baixada' && (
          <span className="badge text-bg-success">
            <i className="bi bi-check-circle-fill me-1"></i>Paga
          </span>
        )}
        {travada && (
          <span className="badge text-bg-danger">
            <i className="bi bi-lock-fill me-1"></i>Com cadeado
          </span>
        )}
      </div>
      <div className="card-body">
        {b.baixa_tiny === 'baixada' ? (
          <dl className="row small mb-0">
            <dt className="col-5 text-secondary fw-normal">Saiu de</dt>
            <dd className="col-7">{b.conta_nome ?? b.conta}</dd>
            <dt className="col-5 text-secondary fw-normal">Pago em</dt>
            <dd className="col-7">{b.data_baixa ? formatarData(b.data_baixa) : '—'}</dd>
            <dt className="col-5 text-secondary fw-normal">Valor</dt>
            <dd className="col-7">{b.valor_baixa != null ? formatarValor(b.valor_baixa) : '—'}</dd>
            <dt className="col-5 text-secondary fw-normal">Baixada</dt>
            <dd className="col-7">
              {b.baixada_em ? formatarData(b.baixada_em) : '—'}
              {b.baixada_por && <div className="text-secondary">por {b.baixada_por}</div>}
            </dd>
          </dl>
        ) : travada ? (
          <div className="alert alert-danger small mb-0">
            <div className="fw-semibold mb-1">
              <i className="bi bi-lock-fill me-1"></i>O Tiny não confirmou a baixa
            </div>
            <p className="mb-2">
              Confira no Tiny se a conta aparece paga. Baixar de novo sem conferir pode
              pagar duas vezes.
            </p>
            {b.erro && <p className="mb-2 text-body-secondary">{b.erro}</p>}
            {pode('despesas.tiny.reabrir') && (
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-sm btn-success" onClick={() => reabrir(true)}>
                  Está paga
                </button>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => reabrir(false)}>
                  Não foi paga
                </button>
              </div>
            )}
          </div>
        ) : b.pode_baixar && pode('despesas.tiny.baixar') ? (
          <>
            {b.erro && <p className="small text-secondary mb-2">{b.erro}</p>}
            <div className="form-floating">
              <select
                id="baixa-conta"
                className="form-select"
                value={conta}
                onChange={(e) => setConta(e.target.value)}
              >
                <option value="">—</option>
                {b.contas.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
              <label htmlFor="baixa-conta">De qual conta saiu o dinheiro</label>
            </div>
            <div className="form-text mb-2">
              {b.sugerida && conta === b.sugerida
                ? 'Sugerida pela forma de pagamento — confirme.'
                : 'As contas são as do Tiny desta loja.'}
            </div>
            <div className="row g-2 mb-3">
              <div className="col-6">
                <CustomCampoData id="baixa-data" label="Pago em" valor={data} onChange={setData} />
              </div>
              <div className="col-6">
                <CustomCampoNumeroMoeda id="baixa-valor" label="Valor pago" valor={valor} onChange={setValor} />
              </div>
            </div>
            <button
              type="button"
              className="btn btn-theme w-100"
              disabled={!conta || !data || !valor}
              onClick={() => setConfirmar(true)}
            >
              <i className="bi bi-cash-coin me-1"></i>Dar baixa no Tiny
            </button>
          </>
        ) : (
          <p className="small text-secondary mb-0">Ainda não foi paga no Tiny.</p>
        )}
      </div>

      <Modal className="casaa-modal" show={confirmar} onHide={() => setConfirmar(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Dar baixa no Tiny</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            A conta a pagar fica <strong>paga</strong> no Tiny, saindo de{' '}
            <strong>{nomeConta}</strong>, em {data ? formatarData(data) : '—'}, no valor de{' '}
            <strong>{valor ? formatarValor(paraBanco(valor) ?? 0) : '—'}</strong>.
          </p>
          <p className="mb-0 text-secondary small">
            É lançamento de verdade no Tiny. Confira a conta antes de confirmar.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-link" onClick={() => setConfirmar(false)}>
            Cancelar
          </button>
          <button type="button" className="btn btn-theme" onClick={baixar} disabled={baixando}>
            {baixando && <span className="spinner-border spinner-border-sm me-2" />}
            Confirmar baixa
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

/** O que a situação do Tiny quer dizer, em português de gente. */
const SITUACAO: Record<string, string> = {
  aberto: 'aberta', pago: 'paga', parcial: 'paga em parte',
  cancelada: 'cancelada', excluida: 'excluída',
};

export function AbaTiny({
  despesaId,
  lojaId,
  aoMudarEnvio,
}: {
  despesaId: string;
  lojaId: string;
  /** Avisa a aba Despesa para travar valor, data, loja… depois do envio. */
  aoMudarEnvio?: (noTiny: boolean) => void;
}) {
  const { pode } = useAuth();
  const [estado, setEstado] = useState<EstadoTiny | null>(null);
  const [opcoes, setOpcoes] = useState<Opcoes>({ categorias: [], contatos: [] });
  const [form, setForm] = useState({
    categoria_tiny: '',
    contato_tiny: '',
    historico_tiny: '',
    competencia: null as string | null,
    cartao_adriana: false,
  });
  const [salvando, setSalvando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [confirmar, setConfirmar] = useState(false);
  const [idTiny, setIdTiny] = useState('');
  const [marcandoJa, setMarcandoJa] = useState(false);

  function aplicar(e: EstadoTiny) {
    setEstado(e);
    aoMudarEnvio?.(e.travada);
    setForm({
      categoria_tiny: e.categoria_tiny ?? '',
      contato_tiny: e.contato_tiny ?? '',
      historico_tiny: e.historico_tiny ?? '',
      competencia: e.competencia,
      cartao_adriana: !!e.cartao_adriana,
    });
  }

  async function carregar(conferirNoTiny = false) {
    try {
      let e = await api.get<EstadoTiny>(`/despesas/${despesaId}/tiny`);
      // ENVIADA? Pergunta ao Tiny se ela ainda está lá (dele, 25/09: "se eu
      // excluir no Tiny, o sistema vai dizer que está lançado"). Excluída ou
      // cancelada lá, o backend libera a despesa e a tela já vem certa.
      if (conferirNoTiny && e.envio_tiny === 'enviada') {
        try {
          await api.post(`/despesas/${despesaId}/tiny/conferir`);
          e = await api.get<EstadoTiny>(`/despesas/${despesaId}/tiny`);
        } catch {
          /* não deu para perguntar: mostra o que se sabia */
        }
      }
      aplicar(e);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível ler o Tiny.');
    }
  }

  useEffect(() => {
    carregar(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [despesaId]);

  useEffect(() => {
    if (!lojaId) return;
    api
      .get<Opcoes>(`/despesas/tiny/opcoes?loja_id=${lojaId}`)
      .then(setOpcoes)
      .catch(() => undefined);
  }, [lojaId]);

  /** As categorias agrupadas pelo grupo do plano de contas do Tiny. */
  const grupos = useMemo(() => {
    const m = new Map<string, Opcoes['categorias']>();
    for (const c of opcoes.categorias) {
      const g = c.grupo ?? 'Sem grupo';
      m.set(g, [...(m.get(g) ?? []), c]);
    }
    return [...m.entries()];
  }, [opcoes.categorias]);

  const contatoEscolhido = opcoes.contatos.find((c) => c.id === form.contato_tiny);

  async function salvar() {
    setSalvando(true);
    try {
      aplicar(
        await api.put<EstadoTiny>(`/despesas/${despesaId}/tiny`, {
          categoria_tiny: form.categoria_tiny || null,
          contato_tiny: form.contato_tiny || null,
          historico_tiny: form.historico_tiny || null,
          competencia: form.competencia,
          cartao_adriana: form.cartao_adriana,
        }),
      );
      toast.success('Salvo.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  }

  async function conferir(sim: boolean) {
    try {
      const e = sim
        ? await api.post<EstadoTiny>(`/despesas/${despesaId}/conferencia`)
        : await api.delete<EstadoTiny>(`/despesas/${despesaId}/conferencia`);
      aplicar(e);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível registrar a conferência.');
    }
  }

  async function enviar() {
    setEnviando(true);
    try {
      const r = await api.post<{ estado: string }>(`/despesas/${despesaId}/tiny/enviar`);
      toast.success(
        r.estado === 'simulada'
          ? 'Simulado — nada foi para o Tiny.'
          : 'Lançada no Tiny.',
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível enviar.');
    } finally {
      setEnviando(false);
      setConfirmar(false);
      carregar();
    }
  }

  async function reabrir(entrou: boolean) {
    try {
      await api.post(`/despesas/${despesaId}/tiny/reabrir`, {
        id_tiny: entrou ? Number(idTiny) : null,
      });
      toast.success(entrou ? 'Registrada como lançada.' : 'Cadeado aberto — pode mandar de novo.');
      setIdTiny('');
      carregar();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível abrir o cadeado.');
    }
  }

  /** "Já está no Tiny" (migration 117): lançada à mão lá — o app não envia. */
  async function marcarJa(marcada: boolean) {
    setMarcandoJa(true);
    try {
      aplicar(
        await api.post<EstadoTiny>(`/despesas/${despesaId}/tiny/ja-lancada`, { marcada }),
      );
      toast.success(marcada ? 'Marcada: já está no Tiny.' : 'Marca desfeita.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível marcar.');
    } finally {
      setMarcandoJa(false);
    }
  }

  if (!estado) {
    return (
      <div className="d-flex align-items-center justify-content-center gap-2 py-5 text-secondary">
        <span className="spinner-border spinner-border-sm" /> Carregando…
      </div>
    );
  }

  const rotulo = ROTULO_ESTADO[estado.envio_tiny] ?? ROTULO_ESTADO.nao_enviada;
  const bloqueado = estado.travada;
  const payload = estado.previa?.payload ?? {};
  const numeroMostrado =
    estado.numero_documento_tiny ?? (payload.numeroDocumento as string | undefined) ?? null;

  return (
    <div className="row g-3">
      <div className="col-lg-8">
        <div className="card adminuiux-card">
          <div className="card-header bg-transparent d-flex align-items-center gap-2">
            <i className="bi bi-send"></i>
            <h6 className="mb-0">O que vai para o Tiny</h6>
          </div>
          <div className="card-body">
            {bloqueado && (
              <p className="text-secondary small">
                Já foi para o Tiny (ou está com cadeado): o que foi enviado não se
                muda por aqui. Correção, no Tiny.
              </p>
            )}
            <fieldset disabled={bloqueado} className="row g-3">
              <div className="col-12">
                <div className="form-floating">
                  <select
                    id="tiny-categoria"
                    className="form-select"
                    value={form.categoria_tiny}
                    onChange={(e) => setForm({ ...form, categoria_tiny: e.target.value })}
                  >
                    <option value="">—</option>
                    {grupos.map(([grupo, itens]) => (
                      <optgroup key={grupo} label={grupo}>
                        {itens.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.nome}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <label htmlFor="tiny-categoria">Categoria no Tiny</label>
                </div>
                <div className="form-text">
                  A lista é da conta do Tiny desta loja.
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-floating">
                  <select
                    id="tiny-contato"
                    className="form-select"
                    value={form.contato_tiny}
                    onChange={(e) => setForm({ ...form, contato_tiny: e.target.value })}
                  >
                    <option value="">—</option>
                    {opcoes.contatos.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nome}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="tiny-contato">Contato (a quem se pagou)</label>
                </div>
                {contatoEscolhido?.quando_usar && (
                  <div className="form-text">{contatoEscolhido.quando_usar}</div>
                )}
              </div>
              <div className="col-sm-6">
                <CustomCampoMesAno
                  id="tiny-competencia"
                  label="Competência"
                  valor={isoParaMes(form.competencia ?? estado.data)}
                  onChange={(d) => setForm({ ...form, competencia: mesParaIso(d) })}
                />
                {!form.competencia && (
                  <div className="form-text">Sem escolher, vale o mês da despesa.</div>
                )}
              </div>
              <div className="col-12">
                <div className="form-floating">
                  <input
                    id="tiny-historico"
                    className="form-control"
                    maxLength={300}
                    placeholder=" "
                    value={form.historico_tiny}
                    onChange={(e) => setForm({ ...form, historico_tiny: e.target.value })}
                  />
                  <label htmlFor="tiny-historico">Histórico no Tiny</label>
                </div>
                <div className="form-text">
                  Em branco, vai o histórico de quem lançou
                  {estado.historico ? `: “${estado.historico}”` : ''}.
                </div>
              </div>
              {/* O cartão de crédito PESSOAL da Adriana (migration 118): só este,
                  marcado à mão — dele, 27/09. Vai com o `ops` e a parcela herda. */}
              <div className="col-12">
                <div className="form-check mb-0">
                  <input
                    id="tiny-cartao-adriana"
                    type="checkbox"
                    className="form-check-input"
                    checked={form.cartao_adriana}
                    onChange={(e) => setForm({ ...form, cartao_adriana: e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="tiny-cartao-adriana">
                    Cartão da Adriana — marcar <span className="font-monospace">cartão-adriana</span> no Tiny
                  </label>
                </div>
              </div>
            </fieldset>
            {!bloqueado && (
              <div className="mt-3">
                <button
                  type="button"
                  className="btn btn-outline-theme"
                  onClick={salvar}
                  disabled={salvando}
                >
                  {salvando && <span className="spinner-border spinner-border-sm me-2" />}
                  Salvar o que vai para o Tiny
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="col-lg-4">
        <div className="card adminuiux-card mb-3">
          <div className="card-header bg-transparent d-flex align-items-center gap-2">
            <i className="bi bi-journal-check"></i>
            <h6 className="mb-0 flex-grow-1">Envio</h6>
            <span className={`badge ${rotulo.classe}`}>
              <i className={`bi ${rotulo.icone} me-1`}></i>
              {rotulo.texto}
            </span>
          </div>
          <div className="card-body">
            {/* EM QUE PÉ ESTÁ, numa frase — dele, 25/09: "cliquei em simular
                e não sei o que eu tenho que fazer agora". */}
            <p className="small mb-3">
              {estado.envio_tiny === 'enviada' ? (
                <>
                  <i className="bi bi-check-circle-fill text-success me-1"></i>
                  Está no Tiny
                  {estado.situacao_tiny
                    ? `, ${SITUACAO[estado.situacao_tiny] ?? estado.situacao_tiny}`
                    : ''}
                  {estado.conferido_tiny_em
                    ? ` (conferido em ${formatarData(estado.conferido_tiny_em)})`
                    : ''}
                  .
                </>
              ) : estado.envio_tiny === 'ja_no_tiny' ? (
                <>
                  <i className="bi bi-check2-all text-success me-1"></i>
                  Foi lançada <strong>à mão</strong> no Tiny — o app não a envia.{' '}
                  {estado.conta_pagar_tiny
                    ? 'Vinculada à conta de lá.'
                    : 'Ainda sem vínculo com a conta de lá.'}
                </>
              ) : estado.cadeado ? (
                <>O Tiny não confirmou o envio — veja abaixo.</>
              ) : estado.envio_tiny === 'simulada' ? (
                <>
                  Simulada: <strong>nada foi para o Tiny</strong>. A prévia abaixo é o
                  que iria.{' '}
                  {estado.escrita_ativa
                    ? 'Para lançar de verdade, use “Mandar para o Tiny”.'
                    : 'O envio real está desligado no servidor.'}
                </>
              ) : (
                <>Ainda não foi para o Tiny.</>
              )}
            </p>
            {/* JÁ ESTÁ NO TINY (migration 117) — UM interruptor, sempre aqui
                (dele, 27/09: "uma hora aparece embaixo, uma hora em cima, isso
                não está bom"). Liga = lançada à mão lá, o app não envia;
                desliga = volta à fila. Some só quando o APP enviou (ou há
                cadeado): aí a despesa está no Tiny por outro caminho. */}
            {pode('despesas.tiny.enviar') &&
              ['nao_enviada', 'simulada', 'ja_no_tiny'].includes(estado.envio_tiny) && (
                <div className="form-check form-switch mb-3">
                  <input
                    id="tiny-ja-lancada"
                    type="checkbox"
                    role="switch"
                    className="form-check-input"
                    checked={estado.envio_tiny === 'ja_no_tiny'}
                    disabled={marcandoJa}
                    onChange={(e) => marcarJa(e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor="tiny-ja-lancada">
                    Já está no Tiny (lançada à mão)
                    <div className="text-secondary">
                      Ligado, o app não manda — evita lançar duas vezes.
                    </div>
                  </label>
                </div>
              )}
            {estado.envio_tiny === 'nao_enviada' && estado.erro_envio_tiny && (
              <p className="small text-secondary mb-3">{estado.erro_envio_tiny}</p>
            )}
            <dl className="row small mb-0">
              <dt className="col-5 text-secondary fw-normal">Documento</dt>
              <dd className="col-7">
                <span className="font-monospace">{numeroMostrado ?? '—'}</span>
                {!estado.numero_documento_tiny && numeroMostrado && (
                  <div className="text-secondary">o próximo — reservado só no envio</div>
                )}
              </dd>
              {estado.parcela_quantidade && (
                <>
                  <dt className="col-5 text-secondary fw-normal">Parcela</dt>
                  <dd className="col-7">
                    {estado.parcela_numero}/{estado.parcela_quantidade}
                    {estado.vencimento && (
                      <div className="text-secondary">vence {formatarData(estado.vencimento)}</div>
                    )}
                  </dd>
                </>
              )}
              {estado.conta_pagar_tiny && (
                <>
                  {/* "ID", não "conta" (dele, 25/09): é o id da conta a pagar
                      lá — o número que se procura no Tiny. */}
                  <dt className="col-5 text-secondary fw-normal">ID no Tiny</dt>
                  <dd className="col-7 font-monospace">{estado.conta_pagar_tiny}</dd>
                </>
              )}
              {estado.enviado_tiny_em && (
                <>
                  <dt className="col-5 text-secondary fw-normal">Enviada em</dt>
                  <dd className="col-7">
                    {formatarData(estado.enviado_tiny_em)}
                    {estado.enviado_por && (
                      <div className="text-secondary">por {estado.enviado_por}</div>
                    )}
                  </dd>
                  <dt className="col-5 text-secondary fw-normal">API</dt>
                  <dd className="col-7">{estado.api_envio_tiny ?? '—'}</dd>
                </>
              )}
              {estado.envio_tiny === 'enviada' && (
                <>
                  <dt className="col-5 text-secondary fw-normal">Marcador</dt>
                  <dd className="col-7">
                    {estado.marcador_tiny === 'aplicado' ? 'aplicado' : 'FALTA — marcar à mão no Tiny'}
                  </dd>
                </>
              )}
            </dl>

            {/* O QUE FOI PARA O TINY, campo a campo (dele, 25/09: "algum lugar
                para visualizar os outros dados, ainda que escondido"). É o
                corpo gravado ANTES do envio — o mesmo que o Tiny recebeu. */}
            {estado.travada && estado.payload_envio_tiny && (
              <details className="mt-3 small">
                <summary className="text-theme-1" style={{ cursor: 'pointer' }}>
                  Ver o que foi enviado
                </summary>
                <dl className="row mt-2 mb-0">
                  {Object.entries(ROTULO_CAMPO).map(([chave, rotulo]) => {
                    const v = (estado.payload_envio_tiny as Record<string, unknown>)[chave];
                    if (v === undefined || v === null || v === '') return null;
                    return (
                      // Fragment, e não <div>: o gutter do `.row` vem de
                      // `.row > *` — um invólucro no meio tirava o recuo.
                      <Fragment key={chave}>
                        <dt className="col-5 text-secondary fw-normal">{rotulo}</dt>
                        <dd className="col-7">{mostrarCampo(chave, v, opcoes)}</dd>
                      </Fragment>
                    );
                  })}
                </dl>
              </details>
            )}

            <p className="small text-secondary mt-3 mb-0">
              <i className="bi bi-paperclip me-1"></i>
              O comprovante fica só aqui no app — não vai para o Tiny.
            </p>

            {estado.cadeado && (
              <div className="alert alert-danger mt-3 mb-0 small">
                <div className="fw-semibold mb-1">
                  <i className="bi bi-lock-fill me-1"></i>O Tiny não confirmou este envio
                </div>
                <p className="mb-2">
                  Procure no Tiny o documento <strong>{estado.numero_documento_tiny}</strong>.
                  Mandar de novo sem conferir pode lançar duas vezes.
                </p>
                {estado.erro_envio_tiny && (
                  <p className="mb-2 text-body-secondary">{estado.erro_envio_tiny}</p>
                )}
                {pode('despesas.tiny.reabrir') ? (
                  <>
                    <div className="input-group input-group-sm mb-2">
                      <input
                        className="form-control"
                        inputMode="numeric"
                        placeholder="Número da conta no Tiny"
                        value={idTiny}
                        onChange={(e) => setIdTiny(e.target.value.replace(/\D/g, ''))}
                      />
                      <button
                        type="button"
                        className="btn btn-success"
                        disabled={!idTiny}
                        onClick={() => reabrir(true)}
                      >
                        Entrou
                      </button>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary w-100"
                      onClick={() => reabrir(false)}
                    >
                      Conferi: não entrou
                    </button>
                  </>
                ) : (
                  <p className="mb-0">Quem pode abrir o cadeado já foi avisado.</p>
                )}
              </div>
            )}

            {!bloqueado && (
              <div className="mt-3">
                {estado.falta ? (
                  <p className="text-secondary small mb-0">
                    <i className="bi bi-info-circle me-1"></i>
                    {estado.falta}
                  </p>
                ) : (
                  <>
                    {/* A CONFERÊNCIA (migration 115): um passo com dono antes do
                        Tiny — dele, 25/09: "alguém verificou tudo, deu o check".
                        Mexer na despesa depois a desfaz. */}
                    {estado.conferida_em ? (
                      <p className="small mb-2">
                        <i className="bi bi-check2-square text-success me-1"></i>
                        Conferida por <strong>{estado.conferida_por}</strong> em{' '}
                        {formatarData(estado.conferida_em)}.{' '}
                        {pode('despesas.conferencia.registrar') && (
                          <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => conferir(false)}>
                            desfazer
                          </button>
                        )}
                      </p>
                    ) : pode('despesas.conferencia.registrar') ? (
                      <div className="mb-2">
                        <p className="small text-secondary mb-2">
                          Antes do Tiny, confira forma de pagamento, categoria, valor e
                          comprovante.
                        </p>
                        <button type="button" className="btn btn-outline-theme w-100" onClick={() => conferir(true)}>
                          <i className="bi bi-check2-square me-1"></i>Conferi — está tudo certo
                        </button>
                      </div>
                    ) : (
                      <p className="small text-secondary mb-2">Aguardando conferência.</p>
                    )}
                    {pode('despesas.tiny.enviar') && (
                      <button
                        type="button"
                        className="btn btn-theme w-100"
                        onClick={() => setConfirmar(true)}
                        disabled={enviando || (estado.escrita_ativa && !estado.conferida_em)}
                        title={estado.escrita_ativa && !estado.conferida_em ? 'Falta conferir' : undefined}
                      >
                        <i className="bi bi-send me-1"></i>
                        {estado.escrita_ativa ? 'Mandar para o Tiny' : 'Simular envio'}
                      </button>
                    )}
                  </>
                )}

                {estado.previa && !estado.previa.disponivel && estado.escrita_ativa && (
                  <p className="text-danger small mt-2 mb-0">
                    A loja não está conectada à API {estado.previa.api} do Tiny.
                    Reconecte em Configurações.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {estado.envio_tiny === 'enviada' && (
          <BlocoBaixa despesaId={despesaId} aoMudar={() => carregar()} />
        )}

        {estado.previa && !bloqueado && (
          <div className="card adminuiux-card">
            <div className="card-header bg-transparent d-flex align-items-center gap-2">
              <i className="bi bi-eye"></i>
              <h6 className="mb-0">Prévia (API {estado.previa.api})</h6>
            </div>
            <div className="card-body">
              <pre className="small mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(estado.previa.payload, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>

      <Modal className="casaa-modal" show={confirmar} onHide={() => setConfirmar(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title as="h5">
            {estado.escrita_ativa ? 'Mandar para o Tiny' : 'Simular envio'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {estado.escrita_ativa ? (
            <p className="mb-0">
              Isto cria uma <strong>conta a pagar de verdade</strong> no Tiny, com o
              documento <strong>{numeroMostrado}</strong>. Se o Tiny não confirmar, a
              despesa fica com cadeado até alguém conferir.
            </p>
          ) : (
            <p className="mb-0">
              O envio ao Tiny está desligado no servidor. Nada vai para lá: fica
              gravado o que seria enviado, para conferir.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-link" onClick={() => setConfirmar(false)}>
            Cancelar
          </button>
          <button type="button" className="btn btn-theme" onClick={enviar} disabled={enviando}>
            {enviando && <span className="spinner-border spinner-border-sm me-2" />}
            {estado.escrita_ativa ? 'Mandar' : 'Simular'}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
